/**
 * Управление сотрудниками: заведение, вход, допуск к участкам.
 *
 * Вход у всех одинаковый — логин и пароль. Списка сотрудников на экране входа
 * нет: он раскрывал бы весь штат любому, кто откроет адрес.
 *
 * Роль после заведения не меняется. Смена роли переносила бы человека между
 * наборами прав разом — при ошибке это тихо ломает и учёт выработки, и доступ.
 * Нужно другое — сотрудник отключается, заводится новый.
 */
import { z } from 'zod';
import { ROLES } from './enums.js';
/**
 * Имя хранится по частям, а не одной строкой.
 *
 * Пока это было одно поле, порядок слов зависел от того, кто заводил: «Иванов
 * Иван» и «Алексей Довыденко» попадали в один список, а сортировался он по
 * первому слову — и имена оказывались вперемешку с фамилиями. Искать человека
 * по фамилии было тоже нечем.
 */
const lastNameField = z
    .string()
    .trim()
    .min(2, 'Укажите фамилию')
    .max(60, 'Слишком длинная фамилия');
const firstNameField = z
    .string()
    .trim()
    .min(2, 'Укажите имя')
    .max(60, 'Слишком длинное имя');
/** Отчество есть не у всех, и требовать его — значит выдумывать данные. */
const middleNameField = z.string().trim().max(60, 'Слишком длинное отчество');
/**
 * Строка для показа. Собирается здесь и только здесь, чтобы во всех двух сотнях
 * мест, где имя выводится, оно выглядело одинаково.
 */
export function composeFullName(parts) {
    return [parts.lastName, parts.firstName, parts.middleName]
        .map((part) => part?.trim() ?? '')
        .filter((part) => part !== '')
        .join(' ');
}
/**
 * Разбор имени, записанного одной строкой.
 *
 * Нужен ровно в одном месте — когда кандидата оформляют сотрудником: в найме
 * имя приходит с площадки вакансий цельной строкой, и делить его там не на чем.
 * Разбор — догадка по пробелам, поэтому оформленную карточку руководитель
 * потом правит руками; сам по себе он ничего не решает.
 */
export function splitFullName(fullName) {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return {
        lastName: parts[0] ?? '',
        firstName: parts[1] ?? '',
        middleName: parts.length > 2 ? parts.slice(2).join(' ') : null,
    };
}
const phoneField = z.string().trim().max(32);
/**
 * Логин приводится к нижнему регистру и здесь, и при входе.
 *
 * Иначе заведённый как `Sidorov` человек набирает на телефоне `sidorov`,
 * получает «Неверный логин или пароль» и после пятой попытки упирается
 * в блокировку — при верном пароле и без единой подсказки, что не так.
 */
const loginField = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, 'Логин короче двух символов')
    .max(64)
    .regex(/^[a-z0-9._-]+$/, 'Логин — латинские буквы, цифры, точка, дефис');
const passwordField = z.string().min(8, 'Пароль короче восьми символов').max(200);
/**
 * Дата начала работы. Задаётся вручную и может быть в прошлом: людей заводят
 * в систему позже, чем они вышли на работу, а от этой даты считается выслуга
 * и надбавки за неё.
 */
const hiredAtField = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД');
/** Роли, для которых участок — это не справочная деталь, а условие работы. */
export const SITE_BOUND_ROLES = ['OPERATOR', 'MASTER'];
/**
 * Кого человек с этой ролью вправе завести.
 *
 * Правило то же, что и при увольнении: администратор ведёт цех, а не штат
 * целиком. Без этого запрет увольнять руководителя обходился бы с другой
 * стороны — администратор завёл бы второго руководителя, сам задал ему пароль
 * и вошёл под ним. Заводить того, кого потом не сможешь убрать, нельзя.
 */
export function creatableRoles(actor) {
    return actor === 'MANAGER' ? ROLES : ['OPERATOR'];
}
export function canCreateRole(actor, target) {
    return creatableRoles(actor).includes(target);
}
export const employeeCreateSchema = z
    .object({
    lastName: lastNameField,
    firstName: firstNameField,
    middleName: middleNameField.optional(),
    phone: phoneField.optional(),
    role: z.enum(ROLES),
    login: loginField,
    password: passwordField,
    hiredAt: hiredAtField,
    /** Допуск к участкам. Назначение на участок без допуска подсвечивается в графике. */
    siteIds: z.array(z.string().uuid()),
})
    .superRefine((value, ctx) => {
    // У мастера участки — это границы его полномочий: он принимает смены
    // и ставит людей только там. Мастер без участков не может ничего,
    // и заводить его так — значит создать человека, которому нечем работать.
    if (SITE_BOUND_ROLES.includes(value.role) && value.siteIds.length === 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['siteIds'],
            message: value.role === 'MASTER'
                ? 'Отметьте участки, за которые отвечает мастер'
                : 'Отметьте хотя бы один участок допуска',
        });
    }
});
/**
 * Правка карточки. Признака «работает» здесь нет намеренно: выключить человека
 * из системы можно только увольнением, а оно требует решения руководителя.
 */
export const employeeUpdateSchema = z.object({
    id: z.string().uuid(),
    lastName: lastNameField.optional(),
    firstName: firstNameField.optional(),
    middleName: middleNameField.optional(),
    phone: phoneField.optional(),
    hiredAt: hiredAtField.optional(),
    siteIds: z.array(z.string().uuid()).optional(),
});
export const employeePasswordSchema = z.object({
    id: z.string().uuid(),
    password: passwordField,
});
/**
 * Увольнение. Причина обязательна: через год «почему ушёл» никто не вспомнит,
 * а решать по возвращении человека придётся по этой записи.
 */
export const dismissalRequestSchema = z.object({
    userId: z.string().uuid(),
    reason: z.string().trim().min(3, 'Укажите причину увольнения').max(1000),
});
export const dismissalDecisionSchema = z.object({
    id: z.string().uuid(),
    approved: z.boolean(),
    comment: z.string().trim().max(1000).optional(),
});
export const employeeRestoreSchema = z.object({ id: z.string().uuid() });
//# sourceMappingURL=staff.js.map